import React from "react";
import {
    AttachMoney,
    AutoAwesomeOutlined,
    HubOutlined,
    Inventory2Outlined,
    PersonOutline,
    QueryStatsOutlined,
    WarningAmberRounded,
} from "@mui/icons-material";
import {ChevronRight} from "@mui/icons-material";
import type {Opportunity, OppSourceKind, OpportunitySource} from "../opportunityIntelligenceDemoData";
import {confidenceColor, formatCompactCurrency, SOURCE_KIND_LABEL, STAGE_LABEL, stageColors, sourceKindColor} from "../opportunityUtils";

export const SOURCE_ICON: Record<OppSourceKind, React.ElementType> = {
    pressure: WarningAmberRounded,
    agent: Inventory2Outlined,
    should_cost: AttachMoney,
    benchmarking: QueryStatsOutlined,
    risk: HubOutlined,
    finder: AutoAwesomeOutlined,
    manual: PersonOutline,
};

/** The initiative-tracker badge (e.g. "SIT-1018 · EBIT-effective"). */
export const SitBadge: React.FC<{ opp: Opportunity }> = ({opp}) => {
    if (!opp.sitId) return null;
    return (
        <span className={`oi-opp-badge${opp.ebitEffective ? ' ebit' : ''}`}>
            {opp.sitId}{opp.ebitEffective ? ' · EBIT-effective' : ''}
            <ChevronRight sx={{fontSize: 13}}/>
        </span>
    );
};

/** Two-line opportunity cell: title + detector/summary + optional SIT badge. */
export const OppTitleCell: React.FC<{ opp: Opportunity; showBadge?: boolean }> = ({opp, showBadge = true}) => (
    <div className="oi-opp-cell">
        <div className="oi-opp-title">{opp.title}</div>
        <div className="oi-opp-summary">{opp.detector} · {opp.summary}</div>
        {showBadge && <SitBadge opp={opp}/>}
    </div>
);

export const SourceChip: React.FC<{ source: OpportunitySource }> = ({source}) => {
    const color = sourceKindColor(source.kind);
    const Icon = SOURCE_ICON[source.kind];
    return (
        <span
            className="oi-source-chip"
            title={SOURCE_KIND_LABEL[source.kind]}
            style={{backgroundColor: `${color}1f`, color}}>
            <Icon className="oi-source-icon"/>
            {source.label}
        </span>
    );
};

export const StagePill: React.FC<{ stage: Opportunity['stage'] }> = ({stage}) => {
    const c = stageColors(stage);
    return (
        <span className="oi-stage-pill" style={{backgroundColor: c.bg, color: c.fg}}>
            {STAGE_LABEL[stage]}
        </span>
    );
};

export const ConfidenceCell: React.FC<{ confidence: number }> = ({confidence}) => (
    <span className="oi-confidence">
        <span className="oi-confidence-dot" style={{backgroundColor: confidenceColor(confidence)}}/>
        {confidence}% confidence
    </span>
);

export const IdentifiedCell: React.FC<{ opp: Opportunity }> = ({opp}) => (
    <span className="oi-identified">
        {formatCompactCurrency(opp.identifiedValue, opp.currency)}
        {opp.realized && <span className="oi-realized-tag">REALIZED</span>}
    </span>
);
